import {
  APICallState,
  FormField,
  PanelContainer,
  useIsDesktop,
} from '@eCommerce/frontend-legacy-shared-library-native';
import CompanionInfoIcon from 'assets/ProfileEmptyStateIcons/icon_companion_info.svg';
import React, {useEffect, useState} from 'react';

import ProfileEmptyStateModule from 'components/ProfileEmptyState/ProfileEmptyStateModule';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';

export interface CompanionSummaryProps {
  companionNumber?: string | undefined;
  onSavePressed: (companionNumber: string) => void;
  apiCallState: APICallState;
  resetAPICallState: () => void;
}

const CompanionSummary: React.FC<CompanionSummaryProps> = ({
  companionNumber,
  onSavePressed,
  apiCallState,
  resetAPICallState,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [companionNumberValue, setCompanionNumberValue] =
    useState(companionNumber);
  const [isInvalidLength, setIsInvalidLength] = useState(false);
  const isDesktop = useIsDesktop();
  const isInvalidNumber = apiCallState === APICallState.INVALID_CODE;

  useEffect(() => {
    setCompanionNumberValue(companionNumber);
  }, [companionNumber]);

  const {trackAdobeEvent} = useTrackEvent();

  const onEditPress = () => {
    setIsEditing(true);
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {
        name: 'edit companion number',
      },
    });
  };

  const saveButtonPress = () => {
    if (!companionNumberValue || companionNumberValue.length < 11) {
      setIsInvalidLength(true);
    } else {
      setIsInvalidLength(false);
      onSavePressed(companionNumberValue);
    }
  };

  const onOverlayAnimationFinish = () => {
    if (isInvalidNumber) {
      return;
    }

    if (apiCallState === APICallState.SUCCESS) {
      setIsEditing(false);
      trackAdobeEvent({
        event: AdobeEvent.click,
        click: {
          name: 'companion number edited',
        },
      });
    }

    resetAPICallState();
  };

  const cancelButtonPress = () => {
    setCompanionNumberValue(companionNumber);
    setIsInvalidLength(false);
    setIsEditing(false);
    resetAPICallState();
  };

  const onChangeText = (value: string) => {
    if (!isNaN(Number(value)) && value.length <= 11) {
      setCompanionNumberValue(value);
    }
  };

  const emptyState = (
    <ProfileEmptyStateModule
      onCTAPress={onEditPress}
      svgIcon={CompanionInfoIcon}
      ctaTitle="Add Companion Number"
      body={
        "Add a companion's Caesars Rewards account. No matter who plays, everybody wins."
      }
      emptyStateTitle="ADD A TRAVEL COMPANION"
      emptyStateIcon="user-friends"
    />
  );

  if (!companionNumber && !isEditing) {
    return emptyState;
  }

  return (
    <PanelContainer
      onEdit={onEditPress}
      onCancel={cancelButtonPress}
      onSave={saveButtonPress}
      showEdit={!isEditing && !!companionNumber}
      hasFooter={isEditing}
      contentMaxWidth={isDesktop ? undefined : '100%'}
      icon={'user-friends'}
      panelTitle={'COMPANION NUMBER'}
      body={
        companionNumber || isEditing
          ? "Add a companion's Caesars Rewards account. No matter who plays, everybody wins."
          : ''
      }
      apiCallState={apiCallState}
      onOverlayAnimationFinish={onOverlayAnimationFinish}>
      <FormField
        nativeID={'travel-companion-number'}
        testID={'travel-companion-number'}
        label={'Travel Companion Number'}
        isInvalidLabel={
          isInvalidLength
            ? 'Companion number must be 11 digits.'
            : 'Invalid companion number'
        }
        isInvalid={isInvalidLength || isInvalidNumber}
        value={companionNumberValue}
        editable={isEditing}
        onChangeText={onChangeText}
        maxLength={11}
        keyboardType="numeric"
      />
    </PanelContainer>
  );
};

export default CompanionSummary;
